import {
  isSuntronicPcbaSerial,
  parseKodiakPcbaPrefixFromSerial,
} from "../mrp/kodiak-pcba-prefix-planned.js";

/** Hyphenated PCBA labels that are not Kodiak five-digit prefix or Suntronic SD barcodes. */
const HYPHENATED_PCBA_SERIAL_RE = /^[A-Za-z0-9?*_]+(?:-[A-Za-z0-9?*_]+)+$/;

/**
 * Compact (no hyphen) Kodiak PCBA barcodes: five-digit order prefix plus a four- or five-digit unit block
 * (for example 466261981, 4655200001). Longer all-digit values are printed device labels, not PCBA.
 */
export const COMPACT_KODIAK_PCBA_MIN_DIGITS = 9;
export const COMPACT_KODIAK_PCBA_MAX_DIGITS = 10;

const KODIAK_HYPHENATED_PCBA_SERIAL_RE = /^([0-9]{5})-/;

export function isKodiakConfigurationPcbaSerial(serial: string): boolean {
  const trimmed = serial.trim();
  if (KODIAK_HYPHENATED_PCBA_SERIAL_RE.test(trimmed)) {
    return parseKodiakPcbaPrefixFromSerial(trimmed) != null;
  }
  if (/^\d+$/.test(trimmed)) {
    return (
      isCompactKodiakConfigurationPcbaSerial(trimmed) &&
      parseKodiakPcbaPrefixFromSerial(trimmed) != null
    );
  }
  return false;
}

/** Ten-digit year-encoded clamp serial (20YY......). */
export function isConfigurationYearEncodedClampSerial(serial: string): boolean {
  return /^20\d{8}$/.test(serial.trim());
}

/**
 * Plain numeric Kodiak PCBA barcode (for example 466261981).
 * Excludes ICCID lengths and year-encoded clamp serials (20YY......).
 */
export function isCompactKodiakConfigurationPcbaSerial(serial: string): boolean {
  const trimmed = serial.trim();
  if (!/^\d+$/.test(trimmed)) return false;
  if (/^[0-9]{19,20}$/.test(trimmed)) return false;
  if (isConfigurationYearEncodedClampSerial(trimmed)) return false;
  if (
    trimmed.length < COMPACT_KODIAK_PCBA_MIN_DIGITS ||
    trimmed.length > COMPACT_KODIAK_PCBA_MAX_DIGITS
  ) {
    return false;
  }
  return parseKodiakPcbaPrefixFromSerial(trimmed) != null;
}

export const CONFIGURATION_PCBA_SERIAL_FORMAT_ERROR =
  "PCBA serial must be a Kodiak order and unit block (hyphen optional), a Suntronic SD barcode, or other hyphenated segments. Placeholders ? * _ are allowed where the label is unreadable.";

export function matchesConfigurationPcbaSerialFormat(serial: string): boolean {
  const trimmed = serial.trim();
  if (trimmed === "") return false;
  if (isConfigurationYearEncodedClampSerial(trimmed)) return false;
  if (isSuntronicPcbaSerial(trimmed)) return true;
  if (KODIAK_HYPHENATED_PCBA_SERIAL_RE.test(trimmed)) {
    return parseKodiakPcbaPrefixFromSerial(trimmed) != null;
  }
  if (isCompactKodiakConfigurationPcbaSerial(trimmed)) return true;
  return HYPHENATED_PCBA_SERIAL_RE.test(trimmed);
}
